// Lightweight Markdown renderer for chatbot answers. Builds React elements
// directly (no innerHTML), supporting headings, paragraphs, lists, tables,
// fenced code blocks, blockquotes, rules and inline bold/italic/code/links.

const BASE_TEXT = {
  fontWeight: 300,
  fontSize: "var(--font-size-14, 0.875rem)",
  color: "var(--eyp-theme-neutral-1000-confident-black, #1a1a24)",
  lineHeight: "1.5",
  margin: 0,
};

const HEADING_SIZES = { 1: "1.25rem", 2: "1.125rem", 3: "1rem", 4: "0.9375rem", 5: "0.875rem", 6: "0.875rem" };

const styles = {
  heading: (level) => ({
    ...BASE_TEXT,
    fontWeight: 500,
    fontSize: HEADING_SIZES[level],
    color: "var(--eyp-color-blue-900-dark-blue, #072e65)",
    lineHeight: "1.4",
  }),
  list: { ...BASE_TEXT, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 4 },
  codeBlock: {
    margin: 0,
    padding: "10px 12px",
    backgroundColor: "#e8edf5",
    borderRadius: 4,
    overflowX: "auto",
    fontFamily: "Consolas, Menlo, monospace",
    fontSize: "0.8125rem",
    lineHeight: "1.45",
    whiteSpace: "pre",
  },
  inlineCode: {
    fontFamily: "Consolas, Menlo, monospace",
    fontSize: "0.8125rem",
    backgroundColor: "#e8edf5",
    borderRadius: 3,
    padding: "1px 4px",
  },
  tableWrap: { overflowX: "auto", maxWidth: "100%" },
  table: { ...BASE_TEXT, borderCollapse: "collapse", width: "100%" },
  th: { textAlign: "left", fontWeight: 500, padding: "6px 8px", borderBottom: "1px solid #afcdfb", backgroundColor: "#e8edf5", verticalAlign: "top" },
  td: { padding: "6px 8px", borderBottom: "1px solid #dde4ee", verticalAlign: "top" },
  blockquote: { ...BASE_TEXT, margin: 0, paddingLeft: 12, borderLeft: "3px solid #afcdfb", color: "#3a3a4a" },
  hr: { border: 0, height: 1, backgroundColor: "var(--border-colors-default-secondary, #afcdfb)", margin: 0, width: "100%" },
};

const SAFE_URL = /^(https?:|mailto:)/i;
const INLINE_RE = /(`[^`]+`)|(\*\*[^*]+\*\*|(?<!\w)__[^_]+__(?!\w))|(\[[^\]]+\]\([^)\s]+\))|(\*[^*\s][^*]*\*|(?<!\w)_[^_\s][^_]*_(?!\w))/;

export function renderInline(text, keyPrefix = "i") {
  const out = [];
  let rest = text;
  let k = 0;
  while (rest) {
    const m = INLINE_RE.exec(rest);
    if (!m) {
      out.push(rest);
      break;
    }
    if (m.index > 0) out.push(rest.slice(0, m.index));
    const token = m[0];
    const key = `${keyPrefix}-${k++}`;
    if (m[1]) {
      out.push(<code key={key} style={styles.inlineCode}>{token.slice(1, -1)}</code>);
    } else if (m[2]) {
      out.push(<strong key={key} style={{ fontWeight: 500 }}>{renderInline(token.slice(2, -2), key)}</strong>);
    } else if (m[3]) {
      const [, label, href] = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token);
      out.push(SAFE_URL.test(href)
        ? <a key={key} href={href} target="_blank" rel="noopener noreferrer">{renderInline(label, key)}</a>
        : <span key={key}>{label}</span>);
    } else {
      out.push(<em key={key}>{renderInline(token.slice(1, -1), key)}</em>);
    }
    rest = rest.slice(m.index + token.length);
  }
  return out;
}

const splitRow = (line) => line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
const isTableSeparator = (line) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line);
const LIST_RE = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;
const isBlockStart = (line, next) =>
  /^\s*```/.test(line) || /^#{1,6}\s/.test(line) || /^\s*>/.test(line) || /^\s*([-*_])(\s*\1){2,}\s*$/.test(line)
  || LIST_RE.test(line) || (line.includes("|") && next !== undefined && isTableSeparator(next));

// Parses Markdown into a flat list of block descriptors.
export function parseBlocks(markdown) {
  const lines = String(markdown || "").replace(/\r\n?/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }

    const fence = /^\s*```(.*)$/.exec(line);
    if (fence) {
      const code = [];
      i++;
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i])) code.push(lines[i++]);
      i++;
      blocks.push({ type: "code", text: code.join("\n") });
      continue;
    }

    const heading = /^(#{1,6})\s+(.*?)\s*#*\s*$/.exec(line);
    if (heading) {
      blocks.push({ type: "heading", level: heading[1].length, text: heading[2] });
      i++;
      continue;
    }

    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
      blocks.push({ type: "hr" });
      i++;
      continue;
    }

    if (line.includes("|") && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const header = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim() && lines[i].includes("|")) rows.push(splitRow(lines[i++]));
      blocks.push({ type: "table", header, rows });
      continue;
    }

    if (/^\s*>/.test(line)) {
      const quote = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) quote.push(lines[i++].replace(/^\s*>\s?/, ""));
      blocks.push({ type: "quote", text: quote.join(" ") });
      continue;
    }

    if (LIST_RE.test(line)) {
      const items = [];
      while (i < lines.length) {
        const m = LIST_RE.exec(lines[i]);
        if (m) {
          items.push({ indent: m[1].replace(/\t/g, "  ").length, ordered: /\d/.test(m[2]), text: m[3] });
          i++;
        } else if (lines[i].trim() && /^\s+/.test(lines[i]) && items.length) {
          items[items.length - 1].text += ` ${lines[i].trim()}`;
          i++;
        } else {
          break;
        }
      }
      blocks.push({ type: "list", items });
      continue;
    }

    const para = [];
    while (i < lines.length && lines[i].trim() && !(para.length && isBlockStart(lines[i], lines[i + 1]))) para.push(lines[i++]);
    blocks.push({ type: "paragraph", lines: para });
  }
  return blocks;
}

// Converts a flat list of items with indentation into nested list trees.
function nestItems(items) {
  const root = { children: [], indent: -1 };
  const stack = [root];
  items.forEach((item) => {
    while (stack.length > 1 && item.indent <= stack[stack.length - 1].indent) stack.pop();
    const node = { ...item, children: [] };
    stack[stack.length - 1].children.push(node);
    stack.push(node);
  });
  return root.children;
}

function ListTree({ nodes, keyPrefix }) {
  const groups = [];
  nodes.forEach((n) => {
    const last = groups[groups.length - 1];
    if (last && last.ordered === n.ordered) last.items.push(n);
    else groups.push({ ordered: n.ordered, items: [n] });
  });
  return groups.map((g, gi) => {
    const Tag = g.ordered ? "ol" : "ul";
    const gk = `${keyPrefix}-g${gi}`;
    return (
      <Tag key={gk} style={styles.list}>
        {g.items.map((n, i) => (
          <li key={`${gk}-${i}`}>
            {renderInline(n.text, `${gk}-${i}`)}
            {n.children.length > 0 && <ListTree nodes={n.children} keyPrefix={`${gk}-${i}`} />}
          </li>
        ))}
      </Tag>
    );
  });
}

function renderBlock(block, i) {
  const key = `b${i}`;
  switch (block.type) {
    case "heading": {
      const Tag = `h${Math.min(block.level + 2, 6)}`;
      return <Tag key={key} style={styles.heading(block.level)}>{renderInline(block.text, key)}</Tag>;
    }
    case "code":
      return <pre key={key} style={styles.codeBlock}><code>{block.text}</code></pre>;
    case "hr":
      return <hr key={key} style={styles.hr} />;
    case "quote":
      return <blockquote key={key} style={styles.blockquote}>{renderInline(block.text, key)}</blockquote>;
    case "table":
      return (
        <div key={key} style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>{block.header.map((c, j) => <th key={j} style={styles.th}>{renderInline(c, `${key}-h${j}`)}</th>)}</tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {block.header.map((_, c) => <td key={c} style={styles.td}>{renderInline(row[c] ?? "", `${key}-${r}-${c}`)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "list":
      return <ListTree key={key} nodes={nestItems(block.items)} keyPrefix={key} />;
    default:
      return (
        <p key={key} style={BASE_TEXT}>
          {block.lines.map((l, j) => (
            <span key={j}>
              {j > 0 && <br />}
              {renderInline(l, `${key}-${j}`)}
            </span>
          ))}
        </p>
      );
  }
}

export function MarkdownMessage({ text }) {
  return <>{parseBlocks(text).map(renderBlock)}</>;
}
